/* eslint-disable @nx/enforce-module-boundaries */
import React, { ReactNode, useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { CalendarProps, ControllerType } from './index.types';
import { DayModifiers, Modifiers, RangeModifier } from 'react-day-picker';
import {
  ADD_BUDDHIST_YEAR_VALUE,
  KO_KR_LOCALE,
  TH_TH_LOCALE,
} from 'libs/mi-offers-components/src/constants/CommonConstants';
import { getDurationBetweenDates } from '../../../utils/DateUtils';
export const VARIATION_VERTICAL = 'VERTICAL';
export const VARIATION_DESKTOP = 'DESKTOP';
export const VARIATION_TABLET = 'TABLET';
export const START_DATE = 'startDate';
export const END_DATE = 'endDate';

export const useCalendar = (props: CalendarProps): ControllerType => {
  const {
    variation,
    startDate,
    endDate,
    focusedInput,
    setDateHandler,
    setFocusedInputHandler,
    nightsLabel,
    nightLabel,
    maxAllowedDays,
    weekdays,
    months,
    weekdaysLong,
    monthsShort,
    weekdaysShort,
    firstDayOfWeek,
    locale,
    yearLabel,
    lengthOfStay,
    validOffersStartDate,
    validOffersEndDate,
  } = props;

  const ALLOWED_DAY_THRESHOLD = maxAllowedDays;

  const [rectHoveredDay, setRectHoveredDay] = React.useState<ClientRect>();
  const [hoverDate, setHoverDate] = React.useState<Date | undefined>(undefined);
  const dateRef = useRef<HTMLDivElement>(null);

  // set selectedDateType to identify selected date type
  type SelectedDateType = 'startDate' | 'endDate' | null;
  const [selectedDateType, setSelectedDateType] = React.useState<SelectedDateType>(START_DATE);

  // set selectedDaysRange & selectedDaysModifier
  const today = new Date();
  const fromDate: Date | undefined = startDate?.toDate() ?? undefined;
  const toDate: Date | undefined = endDate?.toDate() ?? undefined;
  const dateRange: RangeModifier | undefined =
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    startDate !== undefined && endDate !== undefined ? { from: fromDate!, to: toDate! } : undefined;
  // disable prev and next navigation arrow for prev months and after 1 year days
  const fromMonth = today;
  const toMonth = moment(toDate).add(ALLOWED_DAY_THRESHOLD, 'days').toDate();

  // selected date range used to set date range
  const [selectedDaysRange, setSelectedDaysRange] = useState<[Date | undefined, RangeModifier | undefined]>([
    fromDate,
    dateRange,
  ]);

  // selectedDaysModifier used for date styling

  const [selectedDaysModifier, setselectedDaysModifier] = useState<Partial<Modifiers> | undefined>({
    start: fromDate,
    end: toDate,
    select: fromDate,
    hoverRange: undefined,
    fromHover: undefined,
    endHover: undefined,
    fromFocus: undefined,
    toFocus: undefined,
  });
  const displayMonth =
    startDate && variation !== VARIATION_VERTICAL
      ? new Date(moment(startDate).year(), moment(startDate).month())
      : undefined;
  const [initialMonth] = useState(displayMonth);
  const [disabledDays, setDisabledDay] = useState({ before: today, after: toMonth });
  const startDateRef = React.useRef(startDate);
  const endDateRef = React.useRef(endDate);
  const selectedDaysModifierRef = React.useRef(selectedDaysModifier);

  /**
   * update mouse enter event to day cells
   */
  const updateMouseEnterEvent = (attach: boolean): void => {
    // attach mouse enter event on day cell
    const daysEl = document.querySelectorAll(
      '.Selectable .DayPicker-Day:not(.DayPicker-Day--disabled):not(.DayPicker-Day--outside)'
    );
    if (attach) {
      daysEl.forEach(day => {
        day.addEventListener('mouseenter', onDayCellMouseEnter);
      });
    } else {
      daysEl.forEach(day => {
        day.removeEventListener('mouseenter', onDayCellMouseEnter);
      });
    }
  };

  useEffect(() => {
    selectedDaysModifierRef.current = selectedDaysModifier;
  }, [selectedDaysModifier]);

  useEffect(() => {
    setDisabledDay({ before: today, after: toMonth });
    //To bring scroll to start date month when in mobile view.
    if (variation === VARIATION_VERTICAL && startDate) {
      const captions = dateRef.current?.querySelectorAll('.DayPicker-Caption');
      const captionsLength = captions?.length;
      if (captions && captionsLength) {
        for (let i = 0; i < captionsLength; i++) {
          if (
            startDate?.format('MMMM YYYY').toString() === captions[i].querySelector('div')?.innerHTML &&
            dateRef.current
          ) {
            if ('scrollTo' in dateRef.current) {
              dateRef.current.scrollTo({ top: captions[i].getBoundingClientRect().top - 140 });
              break;
            } else {
              const current: HTMLDivElement = dateRef.current;
              current.scrollTop = captions[i].getBoundingClientRect().top - 140;
              break;
            }
          }
        }
      }
    }
    // remove tabindex from calendar wrapper to fix accessibility issues
    const wrapper = document.querySelector('.DayPicker-wrapper');
    wrapper?.removeAttribute('tabindex');
    updateMouseEnterEvent(true);

    return (): void => {
      updateMouseEnterEvent(false);
    };
  }, []);

  // To hide the tooltip once startdate and enddate are selected + add/remove class when start/end date selected/removed respectively
  useEffect(() => {
    if (startDate && endDate) {
      setHoverDate(undefined);
      dateRef.current?.classList.add('startdate-enddate-selected');
    } else {
      dateRef.current?.classList.remove('startdate-enddate-selected');
    }
    if (!startDate && !endDate) {
      setSelectedDaysRange([undefined, undefined]);
      setselectedDaysModifier({
        start: undefined,
        end: undefined,
        select: undefined,
        hoverRange: undefined,
        fromHover: undefined,
        endHover: undefined,
        fromFocus: undefined,
        toFocus: undefined,
      });
    }
    startDateRef.current = startDate;
    endDateRef.current = endDate;
  }, [startDate, endDate]);

  // To hide the tooltip once mouse goes out of calendar
  const onDayMouseLeave = (modifiers: DayModifiers): void => {
    if (modifiers['disabled']) {
      return;
    }
    setHoverDate(undefined);
    setselectedDaysModifier({
      ...selectedDaysModifier,
      hoverRange: undefined,
      fromHover: undefined,
      endHover: undefined,
      fromFocus: undefined,
      toFocus: undefined,
    });
  };

  /**
   *  returns true if tooltip should display
   */
  const shouldDisplayToolTip = (hoveredDate: Date): boolean | null => {
    const momentStartDate = moment(startDate);
    const momentEndDate = moment(endDate);
    const momentHoverDate = moment(hoveredDate);
    return (
      /* if end date is empty and active date field is not on start date, tool tip should display
       * if hover date is greater than start date
       */
      (startDate && !endDate && momentStartDate < momentHoverDate && focusedInput !== START_DATE) ||
      /* if start date is empty and active date field is not on end date,
       * tool tip should display if hover date is less than hover date
       */
      (!startDate && endDate && momentEndDate > momentHoverDate && focusedInput !== END_DATE) ||
      /* if start date and end date are selected, active date field is start date & hover date
       * is less than end date then tool tip should display
       * if start date and end date are selected, active date field is end date & hover date
       * is greater than start date then tool tip should display
       */
      (startDate &&
        endDate &&
        ((momentEndDate > momentHoverDate && focusedInput === START_DATE) ||
          (momentStartDate < momentHoverDate && focusedInput === END_DATE)))
    );
  };
  /**
   * calculates number of nights on hovered state
   */
  const numberOfNightsValue = (): React.ReactNode => {
    if (hoverDate && rectHoveredDay) {
      const curr = hoverDate;
      let d = startDate ?? endDate;

      if (d && shouldDisplayToolTip(hoverDate)) {
        if (startDate && endDate) {
          d = startDate;
        }
        const start = d.format('YYYY-MM-DD');
        const currentDate = moment(curr).format('YYYY-MM-DD');
        // Using Math.abs to get positive numbers
        const calculatedValue = Math.abs(getDurationBetweenDates(start, currentDate));
        const style: React.CSSProperties = {
          position: 'fixed',
          top: `${rectHoveredDay.top - 30}px`,
          left: `${rectHoveredDay.left - 14}px`,
          zIndex: 1,
        };
        return (
          <span style={style} className="Calendar_tooltip">{`${calculatedValue} ${
            calculatedValue > 1 ? nightsLabel : nightLabel
          }`}</span>
        );
      }
    }
    return null;
  };

  /**
   * calculates number of vertical months for mobile view based on days threshold
   */
  const getVerticalMonths = (): number => {
    const startMonths = Number(moment().format('M'));
    const startYearsMonths = Number(moment().format('YYYY')) * 12;
    const start = startMonths + startYearsMonths;
    const lastDate = moment().add(ALLOWED_DAY_THRESHOLD, 'days');
    const lastMonths = Number(lastDate.format('M'));
    const lastYearsMonths = Number(lastDate.format('YYYY')) * 12;
    const last = lastMonths + lastYearsMonths;
    return last - start + 1;
  };

  /**
   *
   * @param e
   */
  const onDayCellMouseEnter = (e: Event): void => {
    const el = e.target as HTMLElement;
    if (el) {
      const day = (el.querySelector('.DayPicker-Day-value') as HTMLSpanElement)?.innerText;
      const month = (el.querySelector('.DayPicker-Day-month-value') as HTMLInputElement)?.value;
      const year = (el.querySelector('.DayPicker-Day-year-value') as HTMLInputElement)?.value;
      const date = new Date(Number(year), Number(month), Number(day));
      if (date) {
        onDayMouseEnter(date, e);
      }
    }
  };

  /**
   * on mouse enter on date set hover date and rect hover day
   * @param day
   * @param event
   */
  const onDayMouseEnter = (day: Date, event: React.MouseEvent<HTMLDivElement> | Event): void => {
    if (selectedDaysModifierRef.current && selectedDaysModifierRef.current['disabled']) {
      return;
    }
    const start = startDateRef?.current;
    const end = endDateRef?.current;
    const target = event.target as HTMLElement;
    setRectHoveredDay(target.getBoundingClientRect());
    setHoverDate(day);
    if (!start || !end) {
      if (start && day > start.toDate()) {
        setselectedDaysModifier({
          ...selectedDaysModifierRef.current,
          hoverRange: { from: start.toDate(), to: day },
          endHover: day,
          fromFocus: start.toDate(),
        });
      } else if (end && day < end.toDate()) {
        setselectedDaysModifier({
          ...selectedDaysModifierRef.current,
          hoverRange: { from: day, to: end.toDate() },
          fromHover: day,
          endFocus: end.toDate(),
        });
      }
    }
  };
  /**
   * set date modifer using date only & update input field focus
   * @param day
   * @param inutFocus
   * @param selectedDateType
   */
  const setSingleDateOnly = (day: Date, inutFocus: SelectedDateType, selectedDateType: SelectedDateType): void => {
    setSelectedDaysRange([day, undefined]);
    setselectedDaysModifier({ ...selectedDaysModifier, select: day, start: undefined, end: undefined });
    setFocusedInputHandler(inutFocus);
    setSelectedDateType(selectedDateType);
  };
  /**
   * set from date
   * @param day
   */
  const setFromDate = (day: Date): void => {
    setSingleDateOnly(day, END_DATE, START_DATE);
    setDateHandler(moment(day), null);
    startDateRef.current = moment(day);
    endDateRef.current = null;
  };
  /**
   * set end date date
   * @param day
   */
  const setEndDate = (day: Date): void => {
    setSingleDateOnly(day, START_DATE, END_DATE);
    setDateHandler(null, moment(day));
    startDateRef.current = null;
    endDateRef.current = moment(day);
  };
  /**
   * set date range using from and to date
   * @param fromDate
   * @param toDate
   */
  const setFromEndDate = (fromDate: Date | undefined, toDate: Date | undefined): void => {
    setSelectedDaysRange([undefined, { from: fromDate, to: toDate }]);
    setselectedDaysModifier({ ...selectedDaysModifier, start: fromDate, end: toDate, select: undefined });
    setDateHandler(moment(fromDate), moment(toDate));
    startDateRef.current = moment(fromDate);
    endDateRef.current = moment(toDate);
  };

  /**
   * on day click handler event.set from and end date.
   * @param day
   * @param modifiers
   */
  const onDayClickHandler = (day: Date, modifiers: DayModifiers): void => {
    if (modifiers['disabled']) {
      return;
    }

    const momentSelectedDay = moment(day);

    // A -when both dates are selected not selected set selected date
    if (!selectedDaysRange[0] && !selectedDaysRange[1]) {
      // when focus is on start set, set selected date as from date
      if (focusedInput === START_DATE) {
        setFromDate(day);
        // when focus is on end date, set selected date as to date
      } else if (focusedInput === END_DATE) {
        setEndDate(day);
      }
    }
    // B-  when both dates are already selected, update both start and end date
    else if (selectedDaysRange[1] !== undefined) {
      const dateRange = selectedDaysRange[1];
      const fromDate = dateRange.from ?? undefined;
      const toDate = dateRange.to ?? undefined;
      const momentFromDate = moment(fromDate);
      const momentToDate = moment(toDate);
      /*
        1. when focus is on start date and selected date is greater than end date,
        clear end date and set selected date as start date
        2. when focus is on end date and selected date is less than from date,
        clear end date and set selected date as start date
        3. for mobile view when check in and checkout dates are already selected,
          set newly selected date as check in date and clear check out date
      */
      if (
        (focusedInput === START_DATE && momentSelectedDay > momentToDate) ||
        (focusedInput === END_DATE && momentSelectedDay < momentFromDate) ||
        variation === VARIATION_VERTICAL
      ) {
        setFromDate(day);
        /*
        1. when focus is on start date and selected date is less than end date, then set both dates
        2. when focus is on end date and selected date is greater than from date, then set both dates
      */
      } else if (focusedInput === START_DATE && momentSelectedDay <= momentToDate) {
        setFromEndDate(day, toDate);
      } else if (focusedInput === END_DATE && momentSelectedDay >= momentFromDate) {
        setFromEndDate(fromDate, day);
      }
      // C - when single date is already selcted.
    } else if (selectedDaysRange[0] !== undefined) {
      const preSelectedDate = selectedDaysRange[0];
      const momentPreSelectedDate = moment(preSelectedDate);
      /*
       1. when focus is on start date and start date is already selected update start date only
       2. when focus is on start date and end date is already selected & selected date is greater than end date then
          set selected date as from date and clear end date
       3. when focus is on end date & from date is already selected & selected day is less than from date then
          set selected date as from date and clear end date
      */
      if (
        (focusedInput === START_DATE && selectedDateType === START_DATE) ||
        (focusedInput === START_DATE && selectedDateType === END_DATE && momentSelectedDay > momentPreSelectedDate) ||
        (focusedInput === END_DATE && selectedDateType === START_DATE && momentSelectedDay < momentPreSelectedDate)
      ) {
        setFromDate(day);
        /*
         when focus is on start date and end date is already selected & end date is greater than selected date then set both dates
        */
      } else if (
        focusedInput === START_DATE &&
        selectedDateType === END_DATE &&
        momentSelectedDay < momentPreSelectedDate
      ) {
        setFromEndDate(day, preSelectedDate);
      }
      // when focus is on end date & from date is already selected & selected date is greater than already selected from date then set both dates
      else if (
        focusedInput === END_DATE &&
        selectedDateType === START_DATE &&
        momentSelectedDay > momentPreSelectedDate
      ) {
        setFromEndDate(preSelectedDate, day);
      }
      // when focus is on end date and end date is already selected then udpate end date
      else if (focusedInput === END_DATE && selectedDateType === END_DATE) {
        setEndDate(day);
      }
    }
  };

  /**
   *
   * @param day
   */
  const renderDay = (day: Date): ReactNode => {
    return (
      <span className={`DayPicker-Day-Cell`}>
        <span className="DayPicker-Day-value">{day?.getDate()}</span>
        <input className="DayPicker-Day-month-value" type="hidden" value={day?.getMonth()} />
        <input className="DayPicker-Day-year-value" type="hidden" value={day?.getFullYear()} />
      </span>
    );
  };

  /**
   *
   * @param day
   */
  const captionElement = (day: { date: Date }): JSX.Element => {
    const monthVal = day.date.getMonth();
    const yearVal = day.date.getFullYear();
    let captionElText = `${months[monthVal]} ${yearVal}`;
    if (locale === KO_KR_LOCALE) {
      captionElText = `${yearVal}${yearLabel} ${months[monthVal]}`;
    }
    if (locale === TH_TH_LOCALE) {
      captionElText = `${months[monthVal]} ${yearVal + ADD_BUDDHIST_YEAR_VALUE}`;
    }
    return (
      <div className="DayPicker-Caption">
        <div>{captionElText}</div>
      </div>
    );
  };

  // Custom disabledDays function to handle dynamic ranges of disabled days
  const isDayDisabled = (day: Date): boolean => {
    const validStartDate = validOffersStartDate ?? moment(disabledDays.before);
    const valideEndDate = validOffersEndDate ?? moment(disabledDays.after);
    if (moment(day).startOf('day') < validStartDate.startOf('day')) {
      return true;
    }

    //  if ( moment(day).startOf('day') > valideEndDate.startOf('day') ) {
    if (
      focusedInput === 'startDate' &&
      moment(day).startOf('day') >
        moment(
          moment(valideEndDate)
            .subtract(lengthOfStay ?? 1, 'days')
            .format('MM/DD/YYYY')
        )
    ) {
      return true;
    } else if (
      focusedInput === 'startDate' &&
      toDate &&
      moment(day).startOf('day') < moment(toDate) &&
      moment(day).startOf('day') >
        moment(
          moment(toDate)
            .subtract(lengthOfStay ?? 1, 'days')
            .format('MM/DD/YYYY')
        )
    ) {
      return true;
    } else if (focusedInput === 'endDate' && moment(day).startOf('day') > moment(valideEndDate)) {
      return true;
    }

    if (fromDate) {
      const minimumStayDate = new Date(fromDate?.getTime() + (lengthOfStay ?? 1) * 24 * 60 * 60 * 1000);
      if (
        focusedInput !== 'startDate' &&
        day < minimumStayDate &&
        moment(minimumStayDate).startOf('day') <= valideEndDate.startOf('day')
      ) {
        return true;
      }
      if (toDate && day > toDate) {
        return false;
      }
    }
    return false;
  };

  return {
    variation,
    dateRef,
    startDate,
    endDate,
    setDateHandler,
    focusedInput,
    setFocusedInputHandler,
    getVerticalMonths,
    numberOfNightsValue,
    onDayMouseEnter,
    onDayClickHandler,
    selectedDaysModifier,
    selectedDaysRange,
    hoverDate,
    onDayMouseLeave,
    fromMonth,
    toMonth,
    renderDay,
    shortWeekdays: weekdays,
    monthsValue: months,
    weekdaysLongName: weekdaysLong,
    initialMonth,
    disabledDays,
    updateMouseEnterEvent,
    weeksdaysShort: weekdaysShort,
    monthsShortValue: monthsShort,
    firstDayOfWeek,
    captionElement,
    isDayDisabled,
  };
};
